import { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { DialogContentText } from '@mui/material';

import {
    IModuleType,
    DeleteModuleDocument,
    UpdateLtiModuleDocument,
    ModuleEditDocument,
} from 'graphql/types';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { PageTitle } from 'common/components/PageTitle';
import { IModuleUpdateDrawerProps } from 'training/types';
import { dayHoursToSeconds } from 'common/utils/formatDate';
import { Loader } from 'common/components/Loader';
import { getModuleOffsetFormValue } from 'training/utils/moduleOffset';
import { AlertDialog } from 'common/components/AlertDialog';
import { Button } from 'common/components/Button';
import { getImageId } from 'common/utils/image';
import {
    ILTIModuleFormProps,
    LTIModuleForm,
} from 'training/components/forms/LTIModuleForm';

export const LTIModuleUpdateDrawer = ({
    id,
    onClose,
}: IModuleUpdateDrawerProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();

    const [showDeleteAlert, setShowDeleteAlert] = useState<boolean>(false);

    const { data, loading, error } = useQuery(ModuleEditDocument, {
        variables: { id, type: IModuleType.Lti },
    });

    useEffect(() => {
        if (error) {
            showApolloError(error);
        }
    }, [error, showApolloError]);

    const [updateModule] = useMutation(UpdateLtiModuleDocument);
    const [deleteModule, { loading: loadingDelete }] =
        useMutation(DeleteModuleDocument);

    const module = data?.module;

    if (loading || !module) return <Loader />;

    if (!module || module.__typename !== 'LTIModule') return null;

    const handleSubmit: ILTIModuleFormProps['onSubmit'] = async (values) => {
        const { image, ltiUrl, offset, ...otherValues } = values;
        const { image: currentImage } = module;

        const moduleValues = {
            ...otherValues,
            // The protocol is removed by the field so we always want to add https:// here.
            ltiUrl: `https://${ltiUrl}`,
            imageId: getImageId(currentImage, image),
            offset: {
                seconds: dayHoursToSeconds(offset?.days, offset?.hours),
                type: offset?.type,
            },
        };

        try {
            await updateModule({
                variables: { id, module: moduleValues },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('moduleActionSuccess.lti.update'), {
            variant: 'success',
        });

        // Close drawer
        onClose?.();

        return;
    };

    const handleDelete = async () => {
        try {
            await deleteModule({
                variables: {
                    id,
                    type: IModuleType.Lti,
                },
                update: (cache) => {
                    cache.evict({ id: `LTIModule:${id}` });
                    cache.gc();
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('moduleActionSuccess.lti.delete'), {
            variant: 'success',
        });

        // Close drawer
        onClose?.();

        return;
    };

    const {
        title,
        description,
        image,
        openOffset,
        moduleGroup: { id: moduleGroupId },
        ltiUrl,
        ltiKey,
        ltiSecret,
        ltiParameters,
    } = module;

    const initialValues = {
        title,
        description,
        image: image || undefined,
        offset: getModuleOffsetFormValue(openOffset),
        // Pass url without protocol
        ltiUrl: ltiUrl && ltiUrl.replace(/(^\w+:|^)\/\//, ''),
        ltiKey,
        ltiSecret,
        ltiParameters,
    };

    return (
        <>
            <PageTitle>{translate('updateLTIModule')}</PageTitle>

            <LTIModuleForm
                edit
                initialValues={initialValues}
                moduleGroupId={moduleGroupId}
                onClose={onClose}
                onDelete={() => setShowDeleteAlert(true)}
                onSubmit={handleSubmit}
            />

            <AlertDialog
                actions={
                    <>
                        <Button
                            autoFocus
                            color="error"
                            loading={loadingDelete}
                            variant="contained"
                            onClick={handleDelete}
                        >
                            {translate('delete')}
                        </Button>
                        <Button
                            disabled={loadingDelete}
                            variant="contained"
                            onClick={() => {
                                setShowDeleteAlert(false);
                            }}
                        >
                            {translate('cancel')}
                        </Button>
                    </>
                }
                open={showDeleteAlert}
                title={translate('deleteLTIModuleMessage.title')}
            >
                <DialogContentText color="text.primary" variant="body2">
                    {translate('deleteLTIModuleMessage.text')}
                </DialogContentText>
            </AlertDialog>
        </>
    );
};
